'use client';

import { useContext, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { clinics } from '@/shared/config/endpoints';
import { City, selectCities } from '@/shared/config/locations';
import { YmapClinicClient } from '@/shared/types/ymaps';

import getUniqueCategories from '../lib/getUniqueCategories';
import { ClinicsContext } from '../ui/Clinics';

const fetcher = (url: string) =>
  fetch(url, { method: 'GET' }).then(async (res) => await res.json());

var options = {
  enableHighAccuracy: true,
  timeout: 5000,
  maximumAge: 0
};

function findNearestCity(cities: City[], latitude: number, longitude: number): string | null {
  let minDistance = Number.MAX_VALUE;
  let nearestCity: string | null = null;

  cities.forEach((city) => {
    const distance = Math.sqrt(
      Math.pow(city.lat - latitude, 2) + Math.pow(city.lng - longitude, 2)
    );
    if (distance < minDistance) {
      minDistance = distance;
      nearestCity = city.name;
    }
  });

  return nearestCity;
}

export function useClinics() {
  const clinicsContext = useContext(ClinicsContext);

  const [llParams, setLlparams] = useState('');
  const [cityParams, setCityParams] = useState('');
  const [currentCity, setCurrentCity] = useState<string | null>(null);

  function success(pos: GeolocationPosition) {
    const crd = pos.coords;
    setLlparams(`${crd.longitude},${crd.latitude}`);

    const idx = selectCities().findIndex(
      (pred) => pred.lat === crd.latitude && pred.lng === crd.longitude
    );
    if (idx === -1) {
      setCurrentCity('Минск');
      setCityParams('Минск');
      return;
    }
    const nearestCity = findNearestCity(selectCities(), crd.latitude, crd.longitude);
    setCurrentCity(nearestCity);
    setCityParams(nearestCity ?? 'Минск');
  }

  const { data, mutate } = useSWR<{
    ymapCompany: YmapClinicClient | null;
  }>(llParams ? `${clinics.getList}?ll=${llParams}&city=${cityParams}` : null, fetcher, {
    fallbackData: { ymapCompany: null },
    revalidateOnFocus: false,
    onSuccess: () => {
      clinicsContext.setLoading(false);
    },
    onError: () => {
      clinicsContext.setLoading(false);
    }
  });

  const getQueryParams = async ({ ll, city }: { ll: string; city: string }) => {
    setLlparams(ll);
    setCityParams(city);
    clinicsContext.setLoading(true);
    await mutate();
  };

  const uniqueCategories = useMemo(
    () => getUniqueCategories(data?.ymapCompany),
    [data?.ymapCompany]
  );

  function errors(err: GeolocationPositionError) {
    setCityParams('Минск');
    setLlparams('27.5473,53.9306');
    // eslint-disable-next-line no-console
    console.warn(`ERROR(${err.code}): ${err.message}`);
  }

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.permissions.query({ name: 'geolocation' }).then(function (result) {
        if (result.state === 'granted') {
          //If granted then you can directly call your function here
          navigator.geolocation.getCurrentPosition(success, errors, options);
        } else if (result.state === 'prompt') {
          //If prompt then the user will be asked to give permission
          navigator.geolocation.getCurrentPosition(success, errors, options);
        } else if (result.state === 'denied') {
          setCityParams('Минск');
          setLlparams('27.547351,53.9306361');
          window.location.href = 'app-settings:location';
        }
      });
    } else {
      alert('Геолокация недоступна в Вашем браузере. Выберите город из списка.');
    }
  }, []);

  useEffect(() => {
    setCurrentCity('Минск');
    setCityParams('Минск');
    setLlparams('27.547351,53.9306361');
  }, []);

  useEffect(() => {
    if (data?.ymapCompany) {
      clinicsContext.setYmapCompany(data?.ymapCompany);
      clinicsContext.setClinicsCount(data?.ymapCompany.clinics.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.ymapCompany]);

  return {
    ymapCompany: clinicsContext.ymapCompany,
    getQueryParams,
    currentCity,
    uniqueCategories
  };
}
